import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface Flower {
  id: string;
  name: string;
  color: string;
  type: string | null;
  stem_length_range: string | null;
  duration_range: string | null;
  image_url: string | null;
  image_path: string | null;
  created_at: string;
  updated_at: string;
}

interface FilterOptions {
  types: string[];
  colors: string[];
  searchTerm?: string;
}

const ITEMS_PER_PAGE = 16;

export function usePaginatedFlowers() {
  const [flowers, setFlowers] = useState<Flower[]>([]);
  const [allFlowers, setAllFlowers] = useState<Flower[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [pageIndex, setPageIndex] = useState(0);
  const [filters, setFilters] = useState<FilterOptions>({ types: [], colors: [] });
  const [isReady, setIsReady] = useState(false); // Nuevo: indica cuando está todo listo
  const { toast } = useToast();

  // Fetch total count with filters
  const fetchTotalCount = useCallback(async (currentFilters: FilterOptions) => {
    try {
      let query = supabase.from('flowers').select('id', { count: 'exact', head: true });
      
      if (currentFilters.types.length > 0) {
        query = query.in('type', currentFilters.types);
      }
      
      if (currentFilters.colors.length > 0) {
        query = query.in('color', currentFilters.colors);
      }

      if (currentFilters.searchTerm) {
        query = query.ilike('name', `%${currentFilters.searchTerm}%`);
      }

      const { count, error: countError } = await query;
      
      if (countError) throw countError;
      
      setTotalCount(count || 0);
    } catch (err) {
      console.error('Error fetching total count:', err);
    }
  }, []);

  // Fetch all flowers (for filter options)
  const fetchAllFlowers = useCallback(async () => {
    try {
      const { data, error: supabaseError } = await supabase
        .from('flowers')
        .select('*')
        .order('name', { ascending: true });

      if (supabaseError) throw supabaseError;
      
      setAllFlowers(data || []);
    } catch (err) {
      console.error('Error fetching all flowers:', err);
    }
  }, []);

  // Fetch paginated flowers
  const fetchFlowers = useCallback(async (currentFilters: FilterOptions, page: number, reset: boolean = false) => {
    try {
      // Completamente sin loading para eliminar cualquier salto visual
      setError(null);
      
      const offset = page * ITEMS_PER_PAGE;
      let query = supabase
        .from('flowers')
        .select('*')
        .order('name', { ascending: true })
        .range(offset, offset + ITEMS_PER_PAGE - 1);
      
      if (currentFilters.types.length > 0) {
        query = query.in('type', currentFilters.types);
      }
      
      if (currentFilters.colors.length > 0) {
        query = query.in('color', currentFilters.colors);
      }

      if (currentFilters.searchTerm) {
        query = query.ilike('name', `%${currentFilters.searchTerm}%`);
      }

      const { data, error: supabaseError } = await query;

      if (supabaseError) throw supabaseError;
      
      if (reset || page === 0) {
        setFlowers(data || []);
        if (!isReady) setIsReady(true); // Marcar como listo cuando tenemos datos
      } else {
        setFlowers(prev => [...prev, ...(data || [])]);
      }
      
    } catch (err) {
      console.error('Error fetching flowers:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch flowers';
      setError(errorMessage);
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      // Solo cambiar loading states si los habíamos activado
      if (reset && flowers.length > 0) {
        setIsLoading(false);
      }
      if (isLoadingMore) {
        setIsLoadingMore(false);
      }
    }
  }, [toast, isReady]);

  // Initial load - completamente inmediato
  useEffect(() => {
    // Cargar datos inmediatamente sin estados de loading
    fetchAllFlowers();
    fetchTotalCount(filters);
    fetchFlowers(filters, 0, false);
  }, []);

  // Update filters and reset pagination
  const updateFilters = useCallback((newFilters: FilterOptions) => {
    setFilters(newFilters);
    setPageIndex(0);
    setFlowers([]);
    setIsLoading(true); // Solo aquí mostramos loading porque es un cambio de filtros
    fetchTotalCount(newFilters);
    fetchFlowers(newFilters, 0, true);
  }, [fetchTotalCount, fetchFlowers]);

  // Load more items
  const loadMore = useCallback(() => {
    if (!isLoadingMore && !isLoading) {
      const nextPage = pageIndex + 1;
      setPageIndex(nextPage);
      fetchFlowers(filters, nextPage, false);
    }
  }, [pageIndex, filters, fetchFlowers, isLoadingMore, isLoading]);

  // Retry function
  const retry = useCallback(() => {
    setError(null);
    setPageIndex(0);
    setFlowers([]);
    fetchTotalCount(filters);
    fetchFlowers(filters, 0, true);
  }, [filters, fetchTotalCount, fetchFlowers]);

  // Computed values
  const currentlyRendered = flowers.length;
  const remaining = Math.max(0, totalCount - currentlyRendered);
  const hasMore = remaining > 0 && !isLoading && !error;
  const isAllLoaded = remaining <= 0 && !isLoading;

  // Get unique types and colors for filters
  const filterOptions = useMemo(() => {
    const types = allFlowers.map(flower => flower.type || flower.name.split(' ')[0]).filter(Boolean);
    const colors = allFlowers.map(flower => flower.color).filter(Boolean);
    
    return {
      availableTypes: [...new Set(types)].sort(),
      availableColors: [...new Set(colors)].sort(),
    };
  }, [allFlowers]);

  return {
    flowers,
    allFlowers,
    isLoading,
    isLoadingMore,
    error,
    totalCount,
    currentlyRendered,
    remaining,
    hasMore,
    isAllLoaded,
    filters,
    filterOptions,
    updateFilters,
    loadMore,
    retry,
    isReady, // Exportar el estado ready
  };
}